import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { MeasurementDto } from '@/features/harness/types';

export interface MeasurementListViewProps {
  /** Measurements to render, already in SequenceNumber (run) order. */
  measurements: MeasurementDto[];
  /** Currently-selected Measurement ids (drives the ticked checkboxes + row highlight). */
  selectedIds: string[];
  /** Toggle a Measurement in/out of the selection. */
  onToggleSelection: (id: string) => void;
  /** Show the "Measurement in progress…" row (live recording only). */
  inProgress?: boolean;
  /**
   * When provided, each row shows a delete affordance that calls this on click. Omit it — as the
   * read-only review view (Story 3.1) does — to render no delete affordance at all.
   */
  onRequestDelete?: (measurement: MeasurementDto) => void;
  /** Select every Measurement (bulk). When both bulk handlers are omitted, no bulk bar renders. */
  onSelectAll?: () => void;
  /** Clear the whole selection (bulk). */
  onClearSelection?: () => void;
}

/**
 * Presentational core of the Measurement list: rows are multi-select (Story 2.1), each showing a
 * SequenceNumber and completion time, an optional in-progress indicator (AC2), and an optional
 * per-row delete affordance (Story 1.6). It reads nothing from the store — the live harness view
 * (`MeasurementList`) and the connection-free review view (Story 3.1) both feed it their own
 * measurements/selection, so the same list renders identically from live state or a REST fetch.
 */
export function MeasurementListView({
  measurements,
  selectedIds,
  onToggleSelection,
  inProgress = false,
  onRequestDelete,
  onSelectAll,
  onClearSelection,
}: MeasurementListViewProps) {
  if (measurements.length === 0 && !inProgress) {
    return null;
  }

  // Bulk Select-all / Clear only earns its space when there's more than one Measurement to act on.
  const showBulk = (onSelectAll || onClearSelection) && measurements.length > 1;
  const allSelected =
    measurements.length > 0 && measurements.every((m) => selectedIds.includes(m.id));
  const noneSelected = selectedIds.length === 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Measurements ({measurements.length})</Typography>
        {showBulk && (
          <Box>
            {onSelectAll && (
              <Button size="small" onClick={onSelectAll} disabled={allSelected}>
                Select all
              </Button>
            )}
            {onClearSelection && (
              <Button size="small" onClick={onClearSelection} disabled={noneSelected}>
                Clear
              </Button>
            )}
          </Box>
        )}
      </Box>
      <List dense disablePadding>
        {inProgress && (
          <ListItem disableGutters>
            <CircularProgress size={16} sx={{ mr: 1.5 }} />
            <ListItemText primary="Measurement in progress…" />
          </ListItem>
        )}
        {measurements.map((m) => {
          const selected = selectedIds.includes(m.id);
          return (
            <ListItem
              key={m.id}
              disableGutters
              disablePadding
              secondaryAction={
                onRequestDelete ? (
                  <IconButton
                    edge="end"
                    aria-label={`Delete measurement #${m.sequenceNumber}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete(m);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            >
              <ListItemButton selected={selected} onClick={() => onToggleSelection(m.id)}>
                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                  {/* Purely a reflection of `selected` — the actual toggle happens via the row's
                      onClick above (so a click anywhere in the row, not just the box, works).
                      `readOnly` (not `onChange`) documents that intentionally and avoids both a
                      React "controlled input without onChange" warning and a double-toggle that
                      a real onChange handler would cause when the click bubbles to the row. */}
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={selected}
                    readOnly
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-label': `Select measurement #${m.sequenceNumber} for comparison`,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`#${m.sequenceNumber}`}
                  secondary={
                    m.completedAt
                      ? new Date(m.completedAt).toLocaleString()
                      : 'Completion time unknown'
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
